// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import handler from '../../../share/util/apiResultValidator';
import vehicle from '../../../share/util/vehicle';
import alert from '../../../share/alert';
import config from '../../../share/config';
import api from '../../../common/api';
import error from '../../../common/nodejs/errors';
import util from '../../../share/util/utils';
import reprogram from '../../../share/util/reprogram';
import loading from '../../../share/components/vue/ProcessingIcon';
const publishedUnitListTestMode = require('../../../data/testMode/publishedUnits.json');

export default {
  data() {
    return {
      processing: false,
      reproResult: {},
      reproTargetInfo: [],
      fieldsConfig: [],
      isShowSpecialAction: false,
      useCcuMsg: false,
      reproCompletedDate: null,
    };
  },

  components: {
    'processing-view': loading
  },

  watch: {

    // クラウドコネクターとの接続状態監視 
    pairingStatus(value) {
      // 失敗
      if (value === config.POLLING_FAILURE) {
        this.processing = false;
        const error = this.pairingError;
        // APIバリデータの戻りを確認
        if (error.validatorResult) {
          // ネットワーク接続エラーとセッション情報エラーは警告メッセージで表示する
          if (error.validatorResult.causeType === handler.causeTypes.networkError ||
            error.validatorResult.causeType === handler.causeTypes.invalidSessionToken) {
            alert.showWarning(this, null, error.message, () => {
              if (error.behaviorType === handler.behaviorTypes.logout) {
                this.logout(this);
              }
              else if (error.behaviorType === handler.behaviorTypes.resetSession) {
                this.resetSession(this);
              }
            });
            return;
          }
        }
        // 上記以外のエラーは予期せぬエラーとしてエラー画面を表示してログアウトする  
        alert.showError(this, null, error.message, () => {
          this.logout(this);
        });
      }
      // 成功
      else if (value === config.POLLING_COMPLETED) {
        this.processing = false;
        // ペアリングに成功した場合は対象のユニット情報のみを更新する。 
        this.backToHome();
      }
    },

    language() {
      this.setDispalyContents();
      const targetUnitDisplayData = vehicle.getTargetUnitDisplayNames(this.unitNames, this.language);
      const reproTargetInfoTemp = [...this.reproTargetInfo];
      for (let index = 0; index < reproTargetInfoTemp.length; ++index) {
        reproTargetInfoTemp[index].targetSystem = targetUnitDisplayData[reproTargetInfoTemp[index].id] || '';
      }
      this.reproTargetInfo = reproTargetInfoTemp;
    }
  },

  computed: {
    ...mapState({
      reproStatus: state => state.reprogram.resultStatus,
      pairingAppId: state => state.netAppConnector.pairingAppId,
      unitInfo: state => state.targetUnit.unitInfo,
      unitDetailInfo: state => state.targetUnit.unitDetailInfo,
      language: state => state.systems.language,
      unitNames: state => state.masterResources.targetUnits,
      userRegion: state => state.userProperty.region,
      cachedErrorInfo: state => state.userProperty.userCache[config.USER_CACHE_REPRO_FAILUER_INFO],
      cachedTargetInfo: state => state.userProperty.userCache[config.USER_CACHE_REPRO_TARGET_INFO],
      // クラウドコネクターの接続状況
      pairingStatus: state => state.netAppConnector.status,
      pairingError: state => state.netAppConnector.error,
      pairingWatcher: state => state.netAppConnector.watcher,
      isTestMode: state => state.netAppConnector.isTestMode,
      vehicleError: state => state.vehicle.error,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      download: state => state.reprogram.download,
      systems: state => state.reprogram.systems,
      ccuReproInfo: state => state.cache.ccuReproInfo,
    }),
  },

  // router prames 対応
  props: ["result"],

  methods: {
    ...mapActions('reprogram', ['setPFCReuseStatus']),
    ...mapActions('netAppConnector', ['pairing', 'disconnectPairing']),
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('cache', ['cacheVehicleInfo', 'cacheDisplayMode']),
    ...mapActions('userProperty', ['setUserCache']),
    ...mapActions('vehicle', ['setVehicleInfo']),

    // 再リプロ実行
    executeAgain: function () {
      this.setPFCReuseStatus(true);
      this.$router.push({ name: 'attention' });
    },

    // リプロエラー画面から診断画面に戻るが押下された場合
    async backErrorToHome() {
      // Vehicle 画面の初期表示は公開一覧
      this.cacheDisplayMode(true);
      // クラウドコネクターとの接続
      this.processing = true;
      // 接続中のクラウドコネクターIDを保持
      const netAppId = this.pairingAppId;
      // 現在接続中のクラウドコネクタを切断する 
      await this.disconnectPairing({ isDeleteCache: false });

      // 再度ペアリングを実施 
      const data = {
        netAppId: netAppId,
        this: this
      };
      this.pairing(data);
    },

    // HOME画面に戻る
    backToHome: function () {
      this.$emit('changeTableTypes', 'campaign');
      // Vehicle 画面の初期表示は公開一覧
      this.cacheDisplayMode(true);
      if (this.isTestMode) {
        // 正常に取得した情報を内部でキャッシュ 
        this.cacheVehicleInfo(this.vehicleInfo);
      }
      // エラーの結果画面でサインアウト・再サインインされたときの表示内容キャッシュをクリア
      this.setUserCache({ key: config.USER_CACHE_REPRO_FAILUER_INFO, value: null });
      // システム画面へ遷移
      let isSuFlow = false;
      if (this.vehicleInfo) {
        isSuFlow = this.vehicleInfo.vehicle.is_su_flow;
      } else {
        isSuFlow = this.cachedTargetInfo.vehicle.is_su_flow;
        this.setVehicleInfo(this.cachedTargetInfo);
      }
      if (isSuFlow) {
        this.$router.push({ name: 'suSystemTable', params: { previousDisplayName: 'result' } });
      } else {
        this.$router.push({ name: 'systemTable', params: { previousDisplayName: 'result' } });
      }
    },

    // リプロ成功完了のテーブルカラムを生成
    setSystemConfig: function () {
      this.fieldsConfig = [
        { key: 'targetSystem', label: this.$t('message.header_repro_success_target_system'), class: 'rpr-result-column-style' },
        { key: 'targetIds', label: this.$t('message.header_repro_success_target_ids'), class: 'rpr-result-column-style ' },
        { key: 'newIds', label: this.$t('message.header_repro_success_new_ids'), class: 'rpr-result-column-style' },
        { key: 'targetPartsNumber', label: this.$t('message.header_repro_success_target_number') },
        { key: 'newPartsNumber', label: this.$t('message.header_repro_success_new_number') }
      ];
      let isSpID = null;
      let isSwid = null;
      if (this.unitDetailInfo) {
        isSpID = this.unitDetailInfo.some((unit) => unit.target_special_id);
        isSwid = this.unitDetailInfo.some((unit) => unit.target_swid);
      }
      if (isSwid) {
        // 対象IDがSWIDの場合、対象ユニットのヘッダ名を変更(CCU, DCM_Ethernet, LCT_G2 など)
        this.fieldsConfig[1].label = this.$t('message.header_vehicle_system_target_swid');
        this.fieldsConfig[2].label = this.$t('message.header_usb_verification_new_swid');
      }
      // 対象がLCT_MAPの場合、対象ユニットのヘッダ名を変更
      else if (isSpID) {
        this.fieldsConfig[1].label = this.$t('message.header_vehicle_system_target_spid');
        this.fieldsConfig[2].label = this.$t('message.label_vehicle_system_new_spid');
      }
    },

    setDispalyContents: function () {
      // リプロ結果の表示用パラメタを初期化
      this.reproResult.result = '';
      this.reproResult.errorCode = '';
      this.reproResult.errorMessage = '';
      this.reproResult.cloudConnectorMsg = '';
      this.reproResult.nextAction = '';
      this.reproResult.pairingTableErrorMsg = '';
      this.reproResult.isSeqDllError = false;

      // 車両情報取得失敗によるエラー画面の設定
      if (this.reproStatus.originalError) {
        this.reproResult.result = 'NG';
        this.reproResult.errorCode = this.reproStatus.errorCode;
        this.reproResult.errorMessage = this.$t(api.getErrorMessageForCode(this.reproStatus.errorCode));
      }
      // リプロ実行結果画面の設定
      else {
        // リプロ成功
        if (this.reproStatus.repro_status === 'success') {
          this.reproResult.result = 'OK';
          this.setSystemConfig();
        }
        // リプロ失敗
        else if (this.reproStatus.repro_status === 'failure') {
          this.reproResult.result = 'NG';
          // リプロ失敗の結果画面からサインアウト・再サインインされた場合は Backend に
          // キャッシュを依頼している userCache の内容を使用して結果画面を再表示する。
          // 1 ECU Inquiry 実行により、すでに pairing テーブルから前回のリプロ失敗情報が
          // 削除されているため、本対応が必要
          const contents = { code: 4526, msg: '', systems: [] };
          if (this.cachedErrorInfo) {
            contents.code = this.cachedErrorInfo.err_code || this.reproStatus.err_code || contents.code;
            contents.msg = this.cachedErrorInfo.err_msg || this.reproStatus.err_msg || contents.msg;
            contents.systems = this.cachedErrorInfo.systems || this.reproStatus.systems || contents.systems;
          }
          this.reproResult.errorCode = contents.code;
          // 表示言語が変更されたときに、リプロ失敗画面のエラーメッセージの表示も切り替える
          this.updateErrorMsg(contents);
        }
      }
    },

    updateErrorMsg: function (err) {
      this.reproResult.errorMessage = this.$t(api.getErrorMessageForCode(err.code));
      const actionID = api.getCauseAndActionForCode(err.code);
      const details = {
        action: this.$t(`${actionID}.action`),
        cause: this.$t(`${actionID}.cause`)
      };
      // CCU 用のエラーメッセージを引き当て
      const hasCCU = err.systems.some(system => system.file_type.toLowerCase() === 'ccu');
      if (hasCCU) {
        switch (err.code) {
          case error.SEQUENCE_APP_ERR_CANNOT_ID_COMMINICATION:
            details.cause = this.$t('action.SEQUENCE_APP_ERR_CANNOT_ID_COMMINICATION_SWID.cause');
            break;
          case error.SEQUENCE_APP_ERR_WRONG_ID_AFTER_WRITE:
            details.cause = this.$t('action.SEQUENCE_APP_ERR_WRONG_ID_AFTER_WRITE_SWID.cause');
            break;
          case error.SEQUENCE_APP_ERR_CANNOT_IGOFFON_ERROR:
            details.action = this.$t('action.SEQUENCE_APP_ERR_CANNOT_IGOFFON_ERROR_SWID.action');
            break;
          default: break;
        }
      }
      // spidの場合のエラーメッセージを表示する
      const hasSpId = err.systems.some(system => system.id_type.toLowerCase() === 'special_id');
      if (hasSpId) {
        switch (err.code) {
          case error.SEQUENCE_APP_ERR_CANNOT_ID_COMMINICATION:
            details.cause = this.$t('action.SEQUENCE_APP_ERR_CANNOT_ID_COMMINICATION_SPID.cause');
            break;
          case error.SEQUENCE_APP_ERR_WRONG_ID_AFTER_WRITE:
            details.cause = this.$t('action.SEQUENCE_APP_ERR_WRONG_ID_AFTER_WRITE_SPID.cause');
            break;
          case error.SEQUENCE_APP_ERR_CANNOT_IGOFFON_ERROR:
            details.action = this.$t('action.SEQUENCE_APP_ERR_CANNOT_IGOFFON_ERROR_SPID.action');
            break;
          default: break;
        }
      }

      if (actionID) {
        let msg = err.msg;
        // 4501,4502 エラーは 16 進数⇒文字列変換
        if (err.code === error.SEQUENCE_APP_ERR_WRONG_ID || err.code === error.SEQUENCE_APP_ERR_WRONG_ID_NEW) {
          // CID/ROMID 判定
          let idType = config.TARGET_ID_TYPE_ROMID;
          if (err.systems && err.systems.length > 0) idType = err.systems[0].id_type;
          // CID/SWID の場合 Hex ⇒ 文字列
          if (idType === config.TARGET_ID_TYPE_CID || idType === config.TARGET_ID_TYPE_SWID || idType === config.TARGET_ID_TYPE_SPECIAL_ID)
            msg = util.hexToString(util.removePadding(msg, false));
        }
        this.reproResult.cloudConnectorMsg = details.cause.replace('$ERROR_MSG$', msg);
        this.reproResult.pairingTableErrorMsg = msg;
        this.reproResult.nextAction = details.action;
        this.reproResult.isSeqDllError = true;
      }
    },

    // Check whether download time needs to be displayed or not
    needToDisplayDownloadTime: function () {
      if (this.isTestMode && this.download.progress === 100) {
        return true;
      }
      return false;
    },

    // Convert the total download time
    convertFileDownloadTime: function () {
      const conversionInfo = reprogram.calculateFileDownloadTime(this.download.total_download_time);
      if (conversionInfo) {
        let downloadTimeDisplay = '';
        if (conversionInfo.minutes > 0) {
          downloadTimeDisplay = `${conversionInfo.minutes} ${this.$t('message.label_repro_execute_dl_unit_min')} `;
        }
        downloadTimeDisplay = `${downloadTimeDisplay}${conversionInfo.seconds} ${this.$t('message.label_repro_execute_dl_unit_sec')}`;
        return downloadTimeDisplay;
      }
      return `0 ${this.$t('message.label_repro_execute_dl_unit_sec')}`;
    },

    // Convert file download speed
    convertFileDownloadSpeed: function () {
      const totalDownloadTime = this.download.total_download_time || 0;
      let downloadSpeed = 0.0;
      if (totalDownloadTime > 0) {
        downloadSpeed = (this.download.total_bytes / 1024) / (totalDownloadTime / 1000);
      }
      const result = reprogram.toFixedDownloadSpeed(downloadSpeed, 0.0);
      return `${result} KB/s`;
    },

    getAlertMsgHtml: function () {
      const btnMsg = '<b>' + this.$t('message.message_result_process_completed_alert_contents_button') + '</b>';
      const ret = this.$t('message.message_result_process_completed_alert_contents', [btnMsg]);
      return ret;
    },

    // Display print preview screen
    displayPrintPreview: function () {
      window.print();
    },

    // Set repro completed date according to browser language setting
    setReproCompletedDate: function () {
      const date = new Date();
      const language = (window.navigator.languages && window.navigator.languages[0]) ||
        window.navigator.language ||
        window.navigator.userLanguage ||
        window.navigator.browserLanguage;
      this.reproCompletedDate = date.toLocaleString(language);
    }
  },

  /* eslint-disable complexity */
  async created() {
    // 言語切り替え時に日時が作り直されないようにcreatedで設定する
    this.setReproCompletedDate();

    // リプロ結果ステータスから、画面の表示内容を設定
    this.reproResult.status = this.result;
    this.setDispalyContents();

    // ユニット詳細情報が存在しない場合は再取得
    let unitDetails = this.unitDetailInfo;
    if (!unitDetails) {
      if (this.reproResult.status === config.STATUS_RPR_FINISHED_SUCCESS) {
        let res = null;
        let fileType = this.ccuReproInfo ? this.ccuReproInfo.fileType : null;
        if (this.isTestMode) {
          const filteredList = publishedUnitListTestMode.filter(item => {
            for (let index = 0; index < item.system_names.length; ++index) {
              for (let sysIndex = 0; sysIndex < this.systems.length; ++sysIndex) {
                if ((item.system_names[index].system_name === this.systems[sysIndex].system_name) ||
                  item.main_system_name === this.systems[sysIndex].system_name) {
                  return true;
                }
              }
            }
            return false;
          });

          let unitList = [];
          if (filteredList.length > 0) {
            const filteredItem = filteredList[0];
            unitList = filteredItem.system_names.map((item, index) => {
              const filteredItemTemp = JSON.parse(JSON.stringify(filteredItem));
              if (filteredItem.system_names.length > 1) {
                const startIndex = 2 * index;
                filteredItemTemp.name = filteredItem.system_names[index].name || filteredItemTemp.name;
                filteredItemTemp.id = filteredItem.system_names[index].id || filteredItem.id;
                // Special handling for EYE
                if (item.system_name === 'EYE_1' || item.system_name === 'EYE_2') {
                  filteredItemTemp.target_romid = index === 0 ? filteredItemTemp.target_romid.substr(0, 10) : filteredItemTemp.target_romid.substr(10);
                  filteredItemTemp.target_cid = null;
                  filteredItemTemp.target_swid = null;
                  filteredItemTemp.new_romid = index === 0 ? filteredItemTemp.new_romid.substr(0, 10) : filteredItemTemp.new_romid.substr(10);
                  filteredItemTemp.new_cid = null;
                  filteredItemTemp.new_swid = null;
                } else {
                  filteredItemTemp.target_romid = filteredItemTemp.target_romid ? `${filteredItemTemp.target_romid.slice(0, -1)}${startIndex}` : null;
                  filteredItemTemp.target_cid = filteredItemTemp.target_cid ? `${filteredItemTemp.target_cid.slice(0, -1)}${startIndex}` : null;
                  filteredItemTemp.target_swid = filteredItemTemp.target_swid ? `${filteredItemTemp.target_swid.slice(0, -1)}${startIndex}` : null;
                  filteredItemTemp.new_romid = filteredItemTemp.new_romid ? `${filteredItemTemp.new_romid.slice(0, -1)}${startIndex + 1}` : null;
                  filteredItemTemp.new_cid = filteredItemTemp.new_cid ? `${filteredItemTemp.new_cid.slice(0, -1)}${startIndex + 1}` : null;
                  filteredItemTemp.new_swid = filteredItemTemp.new_swid ? `${filteredItemTemp.new_swid.slice(0, -1)}${startIndex + 1}` : null;
                }
                filteredItemTemp.target_parts_number = filteredItemTemp.target_parts_number ? `${filteredItemTemp.target_parts_number.slice(0, -1)}${startIndex}` : null;
                filteredItemTemp.new_parts_number = filteredItemTemp.new_parts_number ? `${filteredItemTemp.new_parts_number.slice(0, -1)}${startIndex + 1}` : null;
              }
              return filteredItemTemp;
            });
          }

          fileType = (unitList.length > 0 && unitList[0].target_swid && unitList[0].new_swid) ? config.PFC_FILE_TYPE_CCU : config.PFC_FILE_TYPE_DEFAULT;
          res = {
            data: {
              units: unitList,
              service_news_url: "",
              service_news_name: "",
              translations: [],
              file_type: fileType
            }
          };

          this.processing = false;
          // 成功した場合は情報を設定する。 
          unitDetails = res.data.units;
        } else {
          // 詳細情報の取得
          this.processing = true;
          const endpoint = this.pairingAppId + '/' + this.unitInfo.unitId + '/' + 'reproTargets' + '/' + this.unitInfo.fileId;
          res = await api.getCall(config.REPRO, '/netApps/' + endpoint);
          this.processing = false;
          handler.validate(handler.validateTypes.all, res, this, null, () => {
            // 成功した場合は情報を設定する。 
            unitDetails = res.data.units;
            fileType = res.data.file_type;
          }, () => {
            // 情報取得で失敗してもエラーとはしない。
            unitDetails = null;
          }, null, false);
        }
      }
    }
    // リプロ結果に応じて表示コンポーネントを切り替え
    if (unitDetails && this.reproResult.status === config.STATUS_RPR_FINISHED_SUCCESS) {
      this.reproTargetInfo = [];
      const targetUnitDisplayData = vehicle.getTargetUnitDisplayNames(this.unitNames, this.language);
      for (let index = 0; index < unitDetails.length; index++) {
        const el = unitDetails[index];
        this.reproTargetInfo.push({
          id: el.id,
          targetSystem: targetUnitDisplayData[el.id] || '',
          targetIds: el.target_romid ? el.target_romid : el.target_cid ? el.target_cid : el.target_special_id ? el.target_special_id : el.target_swid,
          newIds: el.new_romid ? el.new_romid : el.new_cid ? el.new_cid : el.new_swid ? el.new_swid : el.new_special_id,
          targetPartsNumber: el.target_parts_number,
          newPartsNumber: el.new_parts_number
        });
      }
    }
    // 追加の対処メッセージを表示するかを判定
    this.isShowSpecialAction = false;
    if (this.reproResult.errorCode === error.SEQUENCE_APP_ERR_CANNOT_IGOFFON_ERROR ||
      this.reproResult.errorCode === error.SEQUENCE_APP_ERR_CANNOT_DIAG_COMMINICATION) {
      this.isShowSpecialAction = true;
      // CCU 特別メッセージへの切り替えフラグを設定
      if (unitDetails && unitDetails.length > 0) {
        this.useCcuMsg = unitDetails[0].file_type.toLowerCase() === 'ccu';
      }
    }
  },

  mounted() {
    // noop
  },

  beforeUnmount() {
    this.$stopInterval(this.pairingWatcher);
  },
};